import * as React from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import {
  createTerminalCall,
  updateTerminalCall,
} from "../../data/terminalcall";
import { useDispatch, useSelector } from "react-redux";
import { TerminalCall } from "../../types/TerminalCall";
import { SelectField } from "../FormFields/RenderSelectField";
import { DateTimeField } from "../FormFields/RenderDateTimeField";
import GetTerminalOptions from "../FormHelpers/TeminalSelectOptions";
import GetVesselOptions from "../FormHelpers/VesselSelectOptions";
import { updateContainer } from "../../data/containers";
import { removeTerminalCall } from "../../data/terminalcall";
import { SelectMultipleField } from "../FormFields/RenderSelectMultipleField";
import GetLabelOptions from "../FormHelpers/LabelSelectOptions";
import { lighten } from '@mui/material/styles';
import { Button, Divider, Grid, Typography } from "@mui/material";

import {
  Close as CloseIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { CheckboxField } from "../FormFields/RenderCheckboxField";
import GetVoyageOptions from "../FormHelpers/VoyageSelectOptions";
import { selectedFilteredVoyage } from "../../selectors/filterSelector";
import { hideDialog } from "../../data/dialog";

const ACTION_TYPE_LOADING = "loading";
const ACTION_TYPE_UNLOADING = "unloading";
const DOCKING_SIDE_STARBOARD = 'starboard';
const DOCKING_SIDE_PORT = 'port';

const MobileTerminalCallForm = ({
  terminalCall,
  terminals,
  vessels,
  container,
  labels,
  loadingUnloading = "",
  voyages,
}) => {
  const dispatch = useDispatch();
  const { voyage } = useSelector(selectedFilteredVoyage);

  const { handleSubmit, control, formState } = useForm({
    defaultValues: terminalCall ?? {
      ...new TerminalCall(),
      voyage: voyage?.['@id'] ?? null,
    },
  });
  const { isSubmitting } = formState;

  const onSubmit: SubmitHandler<TerminalCall> = (data) => {
    if (terminalCall) {
      dispatch(updateTerminalCall(data));
    } else {
      dispatch(createTerminalCall(data));
    }
  };

  const detachTerminalCall = (container, loadingUnloading) => {
    let detachedTerminalCall = container;

    if (loadingUnloading === ACTION_TYPE_LOADING) {
      detachedTerminalCall = {
        ...container,
        loadingTerminal: null,
        loadingTerminalCall: null,
      };
    }
    if (loadingUnloading === ACTION_TYPE_UNLOADING) {
      detachedTerminalCall = {
        ...container,
        unloadingTerminal: null,
        unloadingTerminalCall: null,
      };
    }

    dispatch(updateContainer(detachedTerminalCall));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container maxWidth='80vh'>
        <Grid item xs={12} sm={12}>
          <Grid
            item
            xs={12}
            sx={{
              position: "sticky",
              top: 0,
              backgroundColor: lighten("#1976D2", 0.8),
              zIndex: 10,
              p: 1,
              border: `1px solid #1976D2`,
              borderRadius: "4px",
              "& .MuiInputLabel-root": {
                backgroundColor: "transparent",
                padding: "0 4px",
              },
            }}
          >
            <Grid container flexWrap="nowrap" gap={1} mt={2} mb={2}>
              <Grid item xs={6} sm={6}>
                <SelectField
                  name="voyage"
                  label="Voyage"
                  control={control}
                  options={GetVoyageOptions(voyages)}
                />
              </Grid>
              <Grid item xs={6} sm={6}>
                <SelectField
                  name="vessel"
                  label="Vessel"
                  control={control}
                  options={GetVesselOptions(vessels, false)}
                  required
                />
              </Grid>
            </Grid>
            <Grid item xs={12} sm={12} className="form-fields-container">
              <Grid item xs={12} sm={12}>
                <SelectField
                  name="terminal"
                  label="Terminal"
                  control={control}
                  options={GetTerminalOptions(terminals)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <SelectMultipleField
                  name="labels"
                  label="Labels"
                  control={control}
                  options={GetLabelOptions(labels)}
                  multiple={false}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} marginTop={1} marginBottom={1}>
            <Divider orientation="horizontal" />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" className="form-fields-title">
              ETA / ETD
            </Typography>
            <Grid item xs={12} className="form-fields-container">
              <Grid item xs={12} sm={4} className="form-field-datetime">
                <DateTimeField
                  name="planned"
                  label="Planned"
                  control={control}
                  required={true}
                  setNowOnChange={true}
                />
              </Grid>
              <Grid item xs={12} sm={4} className="form-field-datetime">
                <DateTimeField
                  name="arrival"
                  label="Arrival"
                  control={control}
                  setNowOnChange={true}
                />
              </Grid>
              <Grid item xs={12} sm={4} className="form-field-datetime">
                <DateTimeField
                  name="departure"
                  label="Departure"
                  control={control}
                  setNowOnChange={true}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} marginTop={1} marginBottom={1}>
            <Divider orientation="horizontal" />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" className="form-fields-title">
              Operation Time
            </Typography>
            <Grid item xs={12} className="form-fields-container">
              <Grid item xs={12} sm={4} className="form-field-datetime">
                <DateTimeField
                  name="operationStart"
                  label="Started"
                  control={control}
                  setNowOnChange={true}
                />
              </Grid>
              <Grid item xs={12} sm={4} className="form-field-datetime">
                <DateTimeField
                  name="operationEnd"
                  label="Finished"
                  control={control}
                  setNowOnChange={true}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} marginTop={1} marginBottom={1}>
            <Divider orientation="horizontal" />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" className="form-fields-title">
              Operation
            </Typography>
            <Grid item xs={12} className='form-fields-container'>
                <Grid item xs={12} sm={4}>
                    <CheckboxField
                        name='loadingPlanLocked'
                        label='Lock loading plan'
                        control={ control }
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <SelectField
                    name="dockingSide"
                    label="Docking side"
                    control={control}
                    options={[
                      {
                        value: DOCKING_SIDE_PORT,
                        label: 'Port',
                      },
                      {
                        value: DOCKING_SIDE_STARBOARD,
                        label: 'Starboard',
                      },
                    ]}
                  />
                </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          xs={12}
          className="button-wrapper"
          sx={{
            position: 'sticky',
            bottom: 0,
            backgroundColor: '#fff',
            padding: 2,
            zIndex: 10,
            borderTop: '2px solid #e0e0e0',
          }}
        >
          <Grid
            container
            spacing={1}
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
            flexWrap="nowrap"
          >
            <Grid item>
              <Button
                onClick={() => dispatch(hideDialog())}
                disabled={isSubmitting}
                variant="contained"
                sx={{ height: 45 }}
              >
                <CloseIcon fontSize="small" />
                &nbsp;Close
              </Button>
            </Grid>

            {container && (
              <Grid item>
                <Button
                  onClick={() => detachTerminalCall(container, loadingUnloading)}
                  disabled={isSubmitting}
                  variant="contained"
                  color="error"
                  sx={{ height: 45 }}
                >
                  <DeleteIcon fontSize="small" />
                  &nbsp;Unlink
                </Button>
              </Grid>
            )}

            {terminalCall && (
              <Grid item>
                <Button
                  onClick={() => dispatch(removeTerminalCall(terminalCall))}
                  disabled={isSubmitting}
                  variant="contained"
                  color="error"
                  sx={{ height: 45 }}
                >
                  <DeleteIcon fontSize="small" />
                  &nbsp;Delete
                </Button>
              </Grid>
            )}
          </Grid>

          <Grid container justifyContent="flex-end">
            <Button
              type="submit"
              disabled={isSubmitting}
              variant="contained"
              sx={{ height: 45 }}
            >
              <SaveIcon fontSize="small" />
              &nbsp;Save
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};

export default MobileTerminalCallForm;
